import React, { useContext, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useForm } from 'react-hook-form';
import AppContext from '../context/AppContext';

import { resetPassword } from '../_slice/onboardingSlice';
import CustomInput from '../widgets/CustomInput';
import CustomBtn from '../widgets/CustomBtn';

import logoImg from '../../assets/images/lociLogo.png';
import closeEye from '../../assets/images/close_eye.png';
import openEye from '../../assets/images/open_eye.png';
import rightTick from "../../assets/images/tickRight.png";
import crosstick from "../../assets/images/crosssIcon.png"

function ResetPassword() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { setLoader } = useContext(AppContext);

  const [isPasswordFocused, setIsPasswordFocused] = useState(false);
  const [isConfirmPasswordFocused, setIsConfirmPasswordFocused] = useState(false);

  // Show/hide password state
  const [showPassword, setShowPassword] = useState({
    password: false,
    confirmPassword: false
  });

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors }
  } = useForm({
    mode: 'onChange',
    defaultValues: {
      password: '',
      password_confirmation: ''
    }
  });
  // Watch password value for comparison
  const watchPassword = watch('password');
  const watchPasswordConfirmation = watch('password_confirmation')
  /**
   * Handle form submission.
   * @param {Object} data - Form data containing password and password_confirmation.
   */
  const onSubmit = (data) => {
    const resetData = {
      ...data,
      email: location?.state?.email,
      token: location?.state?.otp
    };

    if (!isPasswordValid(watchPassword)) {
      setIsPasswordFocused(true);
      return;
    }

    if (!isPasswordValid(watchPasswordConfirmation)) {
      setIsConfirmPasswordFocused(true);
      return;
    }

    dispatch(
      resetPassword({
        initialPost: resetData,
        navigate,
        navigatePath: '/organization',
        setLoader
      })
    );
  };

  // Password strength validation function
  const isPasswordValid = (password) =>
    /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*()\-_=+{}|\\[\]:";'<>?,./~`])[A-Za-z\d!@#$%^&*()\-_=+{}|\\[\]:";'<>?,./~`]{12,}$/.test(password);

  const getColor = (password) => (password === '' ? '' : isPasswordValid(password) ? '#292929' : 'red');

  const getValidationMessage = (regex, message, password) => {
    const isValid = regex.test(password);
    const icon = isValid ? <img src={rightTick} alt='' /> : <img src={crosstick} alt='' />;
    const space = '\u00A0\u00A0';
    const colorStyle = isValid ? { color: '#05C680' } : { color: getColor(password) };

    return (
      <div style={{ ...colorStyle, marginTop: "15px" }} >
        {icon} {space} {message}
      </div>
    );
  };

  // Check if all password conditions are met
  const allConditionsMet = isPasswordValid(watch('password'));
  const allConditionsMetConfirm = isPasswordValid(watch('password_confirmation'));

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <div className='onboarding-box'>
        <div className='onboarding-box-header'>
          <img src={logoImg} alt='Logo' className='onboarding-box-header-img' />
          <p className='onboarding-box-header-text'>The Evolution of Safety.</p>
        </div>
        <h2 className='onboardHeading mb-3'>RESET PASSWORD</h2>
        <div className='mb-3 position-relative'>
          <CustomInput
            label='PASSWORD'
            type={showPassword.password ? "text" : "password"}
            icon={true}
            src={showPassword.password ? openEye : closeEye}
            onClick={() =>
              setShowPassword((prev) => ({
                ...prev,
                password: !showPassword.password,
              }))
            }
            classname='custom_input padding_right'
            tabIndex='1'
            {...register('password', {
              required: 'Password required',
            })}
            // onFocus={() => {
            //   if (!allConditionsMet) {
            //     setIsPasswordFocused(true);
            //   }
            // }}
            // onBlur={() => setIsPasswordFocused(false)}
            onFocus={() => setIsPasswordFocused(true)}
            onBlur={() => setIsPasswordFocused(false)}
          />

          {isPasswordFocused && !allConditionsMet && (
            <div style={{ color: getColor(watchPassword) }} className='password_strengthbox'>
              <b className='password_heading mb-4'>Password must be</b>
              {getValidationMessage(/.{12,}$/, ' Minimum 12 characters', watchPassword)}
              {getValidationMessage(/(?=.*[a-z])/, ' At least one lowercase letter', watchPassword)}
              {getValidationMessage(/(?=.*[A-Z])/, ' At least one uppercase letter', watchPassword)}
              {getValidationMessage(/(?=.*\d)/, ' At least one number', watchPassword)}
              {getValidationMessage(/[!@#$%^&*()\-_=+{}|\\[\]:";'<>?,./~`]/, 'At least one special character', watchPassword)}
            </div>
          )}
          {errors.password && <p className='text-danger'>{errors.password.message}</p>}
        </div>

        {/* Confirm Password input field */}
        <div className='mb-3 position-relative'>
          <CustomInput
            label='CONFIRM PASSWORD'
            type={showPassword.confirmPassword ? "text" : "password"}
            icon={true}
            src={showPassword.confirmPassword ? openEye : closeEye}
            onClick={() =>
              setShowPassword((prev) => ({
                ...prev,
                confirmPassword: !showPassword.confirmPassword,
              }))
            }
            classname='custom_input padding_right'
            tabIndex='2'
            {...register('password_confirmation', {
              required: 'Confirm Password required',
              validate: value =>
                value === watchPassword || 'Passwords do not match',
            })}
            onFocus={() => setIsConfirmPasswordFocused(true)}
            onBlur={() => setIsConfirmPasswordFocused(false)}
          />
          {isConfirmPasswordFocused && !allConditionsMetConfirm && (
            <div style={{ color: getColor(watchPasswordConfirmation) }} className='password_strengthbox'>
              <b className='password_heading mb-4'>Password must be</b>
              {getValidationMessage(/.{12,}$/, ' Minimum 12 characters', watchPasswordConfirmation)}
              {getValidationMessage(/(?=.*[a-z])/, ' At least one lowercase letter', watchPasswordConfirmation)}
              {getValidationMessage(/(?=.*[A-Z])/, ' At least one uppercase letter', watchPasswordConfirmation)}
              {getValidationMessage(/(?=.*\d)/, ' At least one number', watchPasswordConfirmation)}
              {getValidationMessage(/[!@#$%^&*()\-_=+{}|\\[\]:";'<>?,./~`]/, 'At least one special character', watchPasswordConfirmation)}
            </div>
          )}
          {errors.password_confirmation && <p className='text-danger'>{errors.password_confirmation.message}</p>}
        </div>

        {/* Footer section */}
        <div className='onboarding-box-footer'>
          <CustomBtn buttonText='Save' type='submit' id='resetBtn' />
        </div>
      </div>
    </form>
  );
}

export default ResetPassword;